/**
 * Attachment upload hook — uploads to the **inMeet backend** (inMeet's own
 * storage, in its role as host app) and returns SDK-shaped `Attachment`s.
 *
 *   const { uploadFiles, uploads, isUploading, clear, removeEntry } =
 *     useAttachmentUpload()
 *
 *   const attachments = await uploadFiles(files)      // 1. upload
 *   await send({ body, attachments })                 // 2. send inline
 *   clear()                                           // 3. reset the panel
 *
 * The SDK stores no bytes — it takes a URL plus metadata and persists that. So
 * each file goes through three steps here: probe it client-side for the
 * metadata a recipient's bubble needs (dimensions, duration, a video poster),
 * PUT the bytes at inMeet's backend, then resolve a durable URL to hand over.
 *
 * Uploads run in parallel and one failure doesn't block the rest. There is no
 * progress reporting here: since the WhatsApp-style outbox
 * (services/upload-outbox.ts) took over conversation sends, this is only the
 * broadcast-list path, whose send waits for the upload. The per-file work is
 * shared with the outbox (`uploadAttachmentFile`).
 */

import { useCallback, useState } from 'react'
import type { Attachment, AttachmentKind } from '../services/messenger.wire'
import { MessengerApiError } from '../services/messenger.api'
import { kindForMime } from '../services/media-probe'
import { uploadAttachmentFile } from '../services/upload-outbox'

export type UploadStatus = 'uploading' | 'done' | 'error'

export interface UploadEntry {
  /** Locally unique id — opaque to consumers, stable across re-renders. */
  id: string
  file: File
  status: UploadStatus
  /** Set on `status === 'done'`. */
  attachment?: Attachment
  /** Set on `status === 'error'`. */
  error?: string
}

export interface UploadRequest {
  file: File
  /** Override the mime-derived kind. Needed for `voice`, which is
   *  indistinguishable from an ordinary `audio/*` file by mime alone. */
  kind?: AttachmentKind
}

interface UseAttachmentUpload {
  uploadFiles: (files: Array<File | UploadRequest>) => Promise<Attachment[]>
  uploads: UploadEntry[]
  isUploading: boolean
  /** Wipe all entries (e.g. after a successful send). */
  clear: () => void
  /** Drop a single entry by id (e.g. user cancels one file). */
  removeEntry: (id: string) => void
}

function localId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return 'up-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
}

function asRequest(input: File | UploadRequest): UploadRequest {
  return input instanceof File ? { file: input } : input
}

export function useAttachmentUpload(): UseAttachmentUpload {
  const [uploads, setUploads] = useState<UploadEntry[]>([])

  const isUploading = uploads.some((u) => u.status === 'uploading')

  const uploadFiles = useCallback(
    async (inputs: Array<File | UploadRequest>): Promise<Attachment[]> => {
      if (inputs.length === 0) return []

      const requests = inputs.map(asRequest)
      // Stage every file as 'uploading' with its own local id so subsequent
      // status updates can match by id even if the user picks the same
      // File twice (some browsers reuse the File ref).
      const entries: UploadEntry[] = requests.map((r) => ({
        id: localId(),
        file: r.file,
        status: 'uploading',
      }))
      setUploads((prev) => [...prev, ...entries])

      const results = await Promise.all(
        entries.map(async (entry, i): Promise<Attachment | null> => {
          const { file } = entry
          const kind = requests[i].kind ?? kindForMime(file.type)
          try {
            const { attachment } = await uploadAttachmentFile(file, kind)
            setUploads((prev) =>
              prev.map((u) => (u.id === entry.id ? { ...u, status: 'done', attachment } : u)),
            )
            return attachment
          } catch (err: unknown) {
            const message = err instanceof MessengerApiError ? err.message : 'upload failed'
            setUploads((prev) =>
              prev.map((u) => (u.id === entry.id ? { ...u, status: 'error', error: message } : u)),
            )
            return null
          }
        }),
      )

      return results.filter((a): a is Attachment => a !== null)
    },
    [],
  )

  const clear = useCallback(() => {
    setUploads([])
  }, [])

  const removeEntry = useCallback((id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id))
  }, [])

  return { uploadFiles, uploads, isUploading, clear, removeEntry }
}
